import { PureComponent, ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import { ReplaceInvisibleCharactersMarkdown } from '@atlassian/bitkit-diff/src/common/util/invisible-character-replacements';

import { ErrorState } from 'src/components/generic-message';
import { Repository } from 'src/components/types';
import urls from 'src/urls/source';
import authRequest from 'src/utils/fetch';

import messages from './error-state.i18n';
import LoadingState from './file-loading';

export type RenderProps = {
  markupAsRawHtml: string;
};

type MarkupFileRendererProps = {
  errorDescription?: ReactNode;
  hash: string | null | undefined;
  path: string;
  render: (props: RenderProps) => ReactNode;
  repository: Repository | null | undefined;
};

type MarkupFileRendererState = {
  error: boolean;
  isLoading: boolean;
  markupAsRawHtml: string | null | undefined;
};

export default class MarkupFileRenderer extends PureComponent<
  MarkupFileRendererProps,
  MarkupFileRendererState
> {
  state: MarkupFileRendererState = {
    error: false,
    isLoading: false,
    markupAsRawHtml: null,
  };

  componentDidMount() {
    this.fetchRenderedMarkup();
  }

  componentDidUpdate(prevProps: MarkupFileRendererProps) {
    const { hash, path, repository } = this.props;
    if (
      path !== prevProps.path ||
      hash !== prevProps.hash ||
      !prevProps.repository ||
      (repository && repository.uuid !== prevProps.repository.uuid)
    ) {
      this.fetchRenderedMarkup();
    }
  }

  fetchRenderedMarkup = async () => {
    this.setState({ isLoading: true });
    const { hash, path, repository } = this.props;

    if (hash && repository) {
      try {
        const fileResponse = await fetch(
          authRequest(
            urls.api.v20.source(repository.full_name, hash, path, {
              format: 'rendered',
            })
          )
        );

        if (!fileResponse.ok) {
          throw Error();
        }

        const { html } = await fileResponse.json();
        const replacedHtml = ReplaceInvisibleCharactersMarkdown(html);
        this.setState({
          error: false,
          isLoading: false,
          markupAsRawHtml: replacedHtml,
        });
      } catch {
        this.setState({
          error: true,
          isLoading: false,
        });
      }
    }
  };

  render() {
    const { error, isLoading, markupAsRawHtml } = this.state;

    if (isLoading) {
      return <LoadingState />;
    }

    if (error) {
      return (
        <ErrorState
          header={<FormattedMessage {...messages.errorHeader} />}
          description={this.props.errorDescription}
          onClick={this.fetchRenderedMarkup}
        />
      );
    }

    return markupAsRawHtml ? this.props.render({ markupAsRawHtml }) : null;
  }
}
